import React, { useState, useEffect } from 'react';
import { LayoutGrid, Rocket, User, Menu, X, LogOut, BarChart3 } from 'lucide-react';
import { logout, getCurrentUser, onAuthStateChange } from '../../services/authUtils';
import { trackButtonClick } from '../../services/analyticsService';

const NavBar = ({ currentPage, setPage, showMobileMenu, setShowMobileMenu, user, onNavigateHome, isAdmin = false }) => {
  const [currentUser, setCurrentUser] = useState(user);

  useEffect(() => {
    const updateUser = async () => {
      const userData = await getCurrentUser();
      setCurrentUser(userData);
    };
    updateUser();
    
    // Listen for auth changes
    const unsubscribe = onAuthStateChange((event, session) => {
      if (session?.user) {
        setCurrentUser(session.user);
      } else {
        setCurrentUser(null);
      }
    });
    
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await trackButtonClick({
        id: 'logout-button',
        text: 'Logout',
        type: 'auth',
        page: currentPage
      }, currentUser?.id);
      
      await logout();
      setCurrentUser(null);
      // Redirect to home
      setPage('journal');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleAuthClick = (pageName) => {
    trackButtonClick({
      id: `${pageName}-button`,
      text: pageName === 'login' ? 'Login' : 'Sign Up',
      type: 'auth',
      page: currentPage
    });
    
    const url = new URL(window.location.href);
    url.searchParams.set('page', pageName);
    window.history.pushState({}, '', url);
    setPage(pageName);
  };

  const handleNavClick = (pageName) => {
    trackButtonClick({
      id: `nav-${pageName}`,
      text:
        pageName === 'journal'
          ? 'Journal'
          : pageName === 'apps'
            ? 'Web Apps'
            : pageName === 'analytics'
              ? 'Analytics'
              : 'About Me',
      type: 'navigation',
      page: currentPage
    });

    if (pageName === 'journal') {
      window.history.pushState({}, '', '/journal');
    } else {
      const url = new URL(window.location.href);
      url.pathname = '/';
      url.searchParams.set('page', pageName);
      window.history.pushState({}, '', url);
    }
    
    setPage(pageName);
    setShowMobileMenu(false);
  };

  const focusRingClass =
    'outline-none focus:outline-none focus-visible:outline-2 focus-visible:outline-indigo-600 focus-visible:outline-offset-2';

  const desktopNavClass = (isActive) =>
    `flex items-center space-x-1.5 text-sm transition-colors ${focusRingClass} ${
      isActive
        ? 'text-stone-900 font-semibold border-b-2 border-indigo-700 pb-0.5'
        : 'text-stone-500 hover:text-stone-900 font-medium border-b-2 border-transparent pb-0.5'
    }`;

  const mobileNavClass = (isActive) =>
    `flex items-center space-x-3 text-left px-3 py-2 text-sm rounded-lg transition-colors ${focusRingClass} ${
      isActive
        ? 'text-stone-900 font-semibold bg-stone-100'
        : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50 font-medium'
    }`;

  return (
    <header className="bg-paper/90 backdrop-blur-md border-b border-stone-200/80 sticky top-0 z-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo/Title Section */}
          <div className="flex items-center min-w-0 flex-1 pr-4">
            <span className="font-serif text-lg sm:text-xl font-semibold tracking-tight text-stone-900 truncate">
              Data, AI, and systems at scale
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-4 lg:space-x-8 items-center">
            <button
              onClick={() => (onNavigateHome ? onNavigateHome() : handleNavClick('journal'))}
              className={desktopNavClass(currentPage === 'journal' || currentPage === 'home')}
            >
              <LayoutGrid className="w-4 h-4" />
              <span>Journal</span>
            </button>
            {isAdmin && (
              <button
                onClick={() => handleNavClick('apps')}
                className={desktopNavClass(currentPage === 'apps')}
              >
                <Rocket className="w-4 h-4" />
                <span>Web Apps</span>
              </button>
            )}
            {isAdmin && (
              <button
                onClick={() => handleNavClick('analytics')}
                className={desktopNavClass(currentPage === 'analytics')}
              >
                <BarChart3 className="w-4 h-4" />
                <span>Analytics</span>
              </button>
            )}
            <button
              onClick={() => handleNavClick('about')}
              className={desktopNavClass(currentPage === 'about')}
            >
              <User className="w-4 h-4" />
              <span>About Me</span>
            </button>
            
            {/* Auth Links */}
            {currentUser ? (
              <div className="flex items-center space-x-2 ml-4 pl-4 border-l border-gray-300">
                <span className="text-sm text-gray-600">{currentUser.email}</span>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-1 py-2 px-3 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2 ml-4 pl-4 border-l border-stone-200">
                <button
                  onClick={() => handleAuthClick('login')}
                  className="text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors"
                >
                  Login
                </button>
                <button
                  onClick={() => handleAuthClick('signup')}
                  className="text-sm font-medium text-white bg-stone-900 hover:bg-stone-800 px-3 py-1.5 rounded-full transition-colors"
                >
                  Sign Up
                </button>
              </div>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => {
              trackButtonClick({
                id: 'mobile-menu-toggle',
                text: showMobileMenu ? 'Close Menu' : 'Open Menu',
                type: 'ui',
                page: currentPage
              });
              setShowMobileMenu(!showMobileMenu);
            }}
            className="md:hidden text-gray-500 hover:text-gray-900 p-2 flex-shrink-0"
          >
            {showMobileMenu ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {showMobileMenu && (
          <div className="md:hidden border-t border-gray-200 py-2">
            <nav className="flex flex-col space-y-1">
              <button
                onClick={() => (onNavigateHome ? onNavigateHome() : handleNavClick('journal'))}
                className={mobileNavClass(currentPage === 'journal' || currentPage === 'home')}
              >
                <LayoutGrid className="w-5 h-5" />
                <span>Journal</span>
              </button>
              {isAdmin && (
                <button
                  onClick={() => handleNavClick('apps')}
                  className={mobileNavClass(currentPage === 'apps')}
                >
                  <Rocket className="w-5 h-5" />
                  <span>Web Apps</span>
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => handleNavClick('analytics')}
                  className={mobileNavClass(currentPage === 'analytics')}
                >
                  <BarChart3 className="w-5 h-5" />
                  <span>Analytics</span>
                </button>
              )}
              <button
                onClick={() => handleNavClick('about')}
                className={mobileNavClass(currentPage === 'about')}
              >
                <User className="w-5 h-5" />
                <span>About Me</span>
              </button>
              
              {/* Mobile Auth Links */}
              {currentUser ? (
                <>
                  <div className="border-t border-gray-200 my-1"></div>
                  <div className="px-4 py-2 text-sm text-gray-600">{currentUser.email}</div>
                  <button
                    onClick={handleLogout}
                    className="text-left px-4 py-2 text-sm text-gray-500 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <div className="border-t border-gray-200 my-1"></div>
                  <button
                    onClick={() => {
                      handleAuthClick('login');
                      setShowMobileMenu(false);
                    }}
                    className="text-left px-4 py-2 text-sm text-gray-500 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => {
                      handleAuthClick('signup');
                      setShowMobileMenu(false);
                    }}
                    className="mx-3 mt-1 text-left px-4 py-2 text-sm text-white bg-stone-900 hover:bg-stone-800 rounded-full transition-colors"
                  >
                    Sign Up
                  </button>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default NavBar;
